import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { AlertTriangle, EyeOff, Loader2, Paperclip, Send } from 'lucide-react';
import { toast } from 'sonner';
import { UploadButtons } from './UploadButtons';
import { prepareFiles } from '../../lib/imageCompress';
import { fmtDateTime } from './orderConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLAG = { fr: '🇫🇷', en: '🇬🇧', zh: '🇨🇳' };

const Bubble = ({ m, isAdmin, onHide }) => {
  const mine = false;
  return (
    <div className={`rounded-lg border p-3 ${m.hidden ? 'opacity-50 border-dashed' : 'border-border'} bg-background`} data-testid={`chat-msg-${m.id}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-semibold">
          {m.sender_role === 'admin' ? 'Jeff Connect' : m.sender_name}
          <span className="text-muted-foreground font-normal"> · {fmtDateTime(m.created_at)}</span>
        </span>
        <span className="flex items-center gap-1">
          {isAdmin && m.risk_flags?.length > 0 && (
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 gap-1">
              <AlertTriangle className="h-3 w-3" />risque
            </Badge>
          )}
          {isAdmin && !m.hidden && (
            <Button variant="ghost" size="icon" className="h-6 w-6" title="Masquer" onClick={() => onHide(m.id)} data-testid={`chat-hide-${m.id}`}>
              <EyeOff className="h-3 w-3" />
            </Button>
          )}
          {m.hidden && <Badge variant="outline" className="text-xs">masqué</Badge>}
        </span>
      </div>
      {m.text && <p className="text-sm whitespace-pre-wrap">{m.text}</p>}
      {Object.entries(m.translations || {}).map(([lang, t]) => (
        <p key={lang} className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{FLAG[lang]} {t}</p>
      ))}
      {m.attachment_id && (
        <a href={`${API}/orders/files/${m.attachment_id}/download`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary underline mt-1">
          <Paperclip className="h-3 w-3" />Pièce jointe
        </a>
      )}
    </div>
  );
};

export const OrderChat = ({ order, onFileSent }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [flow, setFlow] = useState('cabinet_jc');
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/${order.id}/messages`);
      setMessages(data);
    } catch (e) { /* silencieux (polling) */ }
  }, [order.id]);

  useEffect(() => {
    fetchMessages();
    const t = setInterval(fetchMessages, 5000);
    return () => clearInterval(t);
  }, [fetchMessages]);

  const visible = isAdmin ? messages.filter((m) => m.flow === flow) : messages;

  const send = async (attachmentId = null) => {
    if (!text.trim() && !attachmentId) return;
    setBusy(true);
    try {
      await axios.post(`${API}/orders/${order.id}/messages`,
        { text: text.trim(), attachment_id: attachmentId, flow: isAdmin ? flow : undefined });
      setText('');
      fetchMessages();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally { setBusy(false); }
  };

  const attach = async (fileList) => {
    if (!fileList?.length) return;
    setBusy(true);
    setProgress(0);
    try {
      const files = await prepareFiles(fileList);
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const { data } = await axios.post(`${API}/orders/${order.id}/files`, fd, {
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 50),
      });
      for (const att of data) {
        await axios.post(`${API}/orders/${order.id}/messages`,
          { text: '', attachment_id: att.id, flow: isAdmin ? flow : undefined });
      }
      toast.success(`✓ ${data.length > 1 ? `${data.length} fichiers envoyés` : 'Envoyé'}`);
      fetchMessages();
      if (onFileSent) onFileSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi — réessayez");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const hide = async (mid) => {
    try {
      await axios.post(`${API}/orders/${order.id}/messages/${mid}/hide`);
      fetchMessages();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <Card className="border-border" data-testid="order-chat">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">
          {isAdmin ? 'Conversations' : 'Conversation avec Jeff Connect'}
        </CardTitle>
        {isAdmin && (
          <Tabs value={flow} onValueChange={setFlow}>
            <TabsList>
              <TabsTrigger value="cabinet_jc" data-testid="chat-flow-cabinet">Cabinet</TabsTrigger>
              <TabsTrigger value="jc_fabricant" data-testid="chat-flow-lab">Laboratoire</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
          {visible.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Aucun message.</p>}
          {visible.map((m) => <Bubble key={m.id} m={m} isAdmin={isAdmin} onHide={hide} />)}
          <div ref={bottomRef} />
        </div>
        {progress !== null && (
          <div className="space-y-1" data-testid="chat-upload-progress">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground">Envoi en cours... {progress}%</p>
          </div>
        )}
        <div className="flex gap-2 items-center">
          <UploadButtons onFiles={attach} disabled={busy} compact />
          <Input value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
            placeholder="Votre message..." data-testid="chat-text-input" />
          <Button onClick={() => send()} disabled={busy} data-testid="chat-send-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Traduction automatique fournie — le texte original fait référence. Toute transmission de
          coordonnées est détectée et signalée. Jeff Connect est l'intermédiaire unique.
        </p>
      </CardContent>
    </Card>
  );
};
